/**
 * `createClient<paths>(config)` — public entry point. Returns an
 * `openapi-fetch` client wired through the configured ky instance. Apps
 * pass the generated `paths` type from `types/schema` to get full
 * call-site autocomplete:
 *
 *     const client = createClient<paths>({ ... });
 *     const { data, error } = await client.GET('/api/v2/alert-history/', {
 *       params: { query: { ids: [...] } },
 *     });
 *
 * The lib does not own the `paths` type — apps import their own copy from
 * `@tiaki/http-client/types` (the regenerated file) so a serializer change
 * fails type-check at the call site, not at the lib boundary.
 *
 * Replacement note: if `openapi-fetch` is ever swapped for a hand-typed
 * fetch wrapper, this file is the only LOC the call sites care about.
 * `transport.ts` and the middleware stack stay put.
 */
import createOpenApiFetchClient, { type Client } from 'openapi-fetch';
import { buildTransport, type TransportConfig } from './transport';
import {
  ApiValidationError,
  parseValidationError,
} from './middleware/validation-errors';
import {
  buildDownloadFile,
  type DownloadFileOptions,
  type DownloadResult,
} from './downloads';
import type { KyInstance } from 'ky';
import type { RefreshResult } from './middleware/auth-refresh';

export type { TransportConfig } from './transport';
export type { DownloadFileOptions, DownloadResult } from './downloads';

export interface CreateClientConfig extends TransportConfig {
  /**
   * When true (default) the client surfaces DRF 400 field errors as
   * `ApiValidationError` rejections. SMS forms rely on this behavior; AIM
   * gets it for free post-migration. Set to false to handle 400s manually.
   */
  surfaceValidationErrors?: boolean;
}

/**
 * Returned client = the typed openapi-fetch surface plus a `downloadFile`
 * helper bound to the same transport. Apps consume `client.GET/POST/...`
 * for JSON endpoints and `client.downloadFile(...)` for blob exports
 * (alert-history PDF/CSV/JSON, snapshot exports, etc.).
 *
 * The constraint matches openapi-fetch's own `Client<Paths extends {}>`
 * upstream signature — the generated `paths` interface from
 * `openapi-typescript` does not satisfy `Record<string, unknown>` because
 * it lacks an index signature, but it does satisfy the looser `{}`.
 */
export type HttpClient<Paths extends {}> = Client<Paths> & {
  downloadFile: (
    endpoint: string,
    options?: DownloadFileOptions,
    fallbackFilename?: string,
  ) => Promise<DownloadResult>;
  /**
   * Underlying ky instance. Exposed for the few call-sites (FormData
   * uploads, custom streaming endpoints) that legitimately need to talk
   * to the transport directly. Prefer the typed methods otherwise.
   */
  ky: KyInstance;
  /**
   * Manually refresh the access token through the configured refresh path
   * (`refreshEndpoint` or `refresh` callback). Single-flight: collapses with
   * any in-flight auto-refresh from the auth-refresh middleware so
   * userHandlers' permissions-changed handler + a concurrent 401 do not
   * fan out into two refresh round-trips. Persists the new token to storage
   * and fires `onTokenRefreshComplete` listeners on success.
   *
   * The one lib-owned refresh implementation — apps route manual refreshes
   * here rather than through their own raw-axios helpers. It bypasses the
   * lib's middleware so a 401-from-refresh can't recurse.
   */
  refreshAccessToken: () => Promise<RefreshResult>;
};

/**
 * Build the typed openapi-fetch client. Generic `Paths` is the
 * `paths` type emitted by `openapi-typescript` from the Django OpenAPI
 * schema; pass it at the call site for typed endpoints.
 */
export function createClient<Paths extends {}>(
  config: CreateClientConfig,
): HttpClient<Paths> {
  const { ky, refreshAccessToken } = buildTransport(config);
  const surfaceValidationErrors = config.surfaceValidationErrors ?? true;

  // openapi-fetch accepts any fetch-like callable. ky is fetch-compatible
  // when called as `ky(input, init)` — it returns a Response just like
  // global fetch. Wrapping it lets us interpose the validation-error map.
  const fetchImpl: typeof fetch = async (input, init) => {
    let response: Response;
    try {
      response = await ky(input as Request | string | URL, init);
    } catch (err) {
      // ky throws HTTPError on non-2xx (when `throwHttpErrors !== false`).
      // We want raw responses so openapi-fetch's `{data, error}` discriminated
      // union surfaces 4xx/5xx properly. Re-throw network/timeout/abort errors;
      // synthesize the response for HTTPErrors.
      //
      // ky 2 pre-consumes the error body into `error.data` (JSON-parsed for
      // JSON content types, plain text otherwise, undefined when empty), which
      // leaves `error.response`'s body unreadable. Rebuild a readable Response
      // from `data` + the original status/headers so openapi-fetch and the
      // validation peek below can still parse it.
      const maybeHttpError = err as { response?: Response; data?: unknown };
      if (maybeHttpError && maybeHttpError.response instanceof Response) {
        const consumed = maybeHttpError.response;
        const { data } = maybeHttpError;
        const body =
          data === undefined
            ? null
            : typeof data === 'string'
              ? data
              : JSON.stringify(data);
        response = new Response(body, {
          status: consumed.status,
          statusText: consumed.statusText,
          headers: consumed.headers,
        });
      } else {
        throw err;
      }
    }

    if (
      surfaceValidationErrors &&
      response.status === 400 &&
      response.headers.get('content-type')?.includes('application/json')
    ) {
      // Peek at the body so the upstream `error` branch still gets a parsed
      // payload (clone first; the original body must still be readable by
      // openapi-fetch). On a validation match, throw — the throw becomes a
      // promise rejection at the call site.
      const peek = response.clone();
      try {
        const body = await peek.json();
        const validationError = parseValidationError(body, 400);
        if (validationError) {
          throw validationError;
        }
      } catch (err) {
        if (err instanceof ApiValidationError) throw err;
        // JSON parse failed — fall through to the raw response.
      }
    }

    return response;
  };

  const client = createOpenApiFetchClient<Paths>({
    baseUrl: config.baseURL,
    fetch: fetchImpl,
  }) as HttpClient<Paths>;

  client.downloadFile = buildDownloadFile(ky);
  client.ky = ky;
  client.refreshAccessToken = refreshAccessToken;
  return client;
}
